import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcA = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Demystifying Form 990 Schedule A - Tax-Exempt Organizations"
          description="Discover essential details on IRS Form 990 Schedule A for tax-exempt organizations. Simplify nonprofit reporting. Ensure compliance with IRS guidelines."
          keywords="Form 990-EZ Schedule A, Public charity support, Charitable contributions, Tax-exempt organizations, 501(c)(3) organizations"
          canonical="resources/tax-exempt/form-990-schedule-a/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule A</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your organization’s public charity status with Schedule A using
          TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990scha`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-a/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule A at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule A
                        </strong>{' '}
                        is used to report an organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          public charity status
                        </strong>{' '}
                        and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          public support,{' '}
                        </strong>
                        helping the IRS determine whether it qualifies as a
                        public charity rather than a private foundation.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/501c3-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Section 501(c)(3)
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>
                          public charities,{' '}
                        </strong>
                        certain tax-exempt organizations, and eligible{' '}
                        <strong style={{ fontWeight: 600 }}>
                          nonexempt charitable trusts
                        </strong>{' '}
                        that file{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-990-instructions/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990
                        </Link>{' '}
                        or{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-ez/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 990-EZ
                        </Link>{' '}
                        must generally attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule A.</strong>{' '}
                        Private foundations file{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-pf/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-PF
                        </Link>{' '}
                        instead.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Key Reporting:
                        </strong>{' '}
                        Schedule A reports the organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          public charity classification, public support
                          calculations
                        </strong>
                        , and information required to support its tax-exempt
                        status.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule A?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule A filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule A filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="what"
                >
                  What is Schedule A?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>
                    Schedule A, <i>Public Charity Status and Public Support</i>
                  </strong>{' '}
                  is a supplementary schedule that accompanies the standard Form
                  990/990-EZ and is filed annually by tax-exempt organizations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you're wondering about Schedule A on a tax return, it is
                  the attachment used by 501(c)(3) organizations to report their
                  public charity status and public support details to the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Filing schedule A along with your information return allows
                  you to report critical data on your{' '}
                  <strong style={{ fontWeight: 600 }}>
                    public charity status, support, and revenue
                  </strong>
                  , helping to ensure transparency and compliance with IRS
                  regulations.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file Schedule A?
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Not all tax-exempt organizations are required to file{' '}
                  <strong style={{ fontWeight: 600 }}>
                    {/* <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990
                    </Link>{' '} */}
                    Schedule A
                  </strong>
                  . To determine if your organization must file this schedule,
                  consider the following:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Section 501(c)(3) and Public Charities:
                    </strong>{' '}
                    Organizations that are described in section 501(c)(3) and
                    qualify as public charities, as opposed to private
                    foundations, are generally required to file Schedule A.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>Gross Receipts:</strong>{' '}
                    If your organization&apos;s gross receipts exceed $50,000,
                    you will likely need to{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      file Form 990
                    </Link>{' '}
                    Schedule A. However, smaller organizations with gross
                    receipts below this threshold may be exempt.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Nonexempt Charitable Trusts:
                    </strong>{' '}
                    Nonexempt charitable trusts described in section 4947(a)(1)
                    that aren&apos;t treated as private foundations.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Public Support Test:
                    </strong>{' '}
                    Organizations relying on public support rather than
                    endowments must file Schedule A. The support test evaluates
                    the percentage of public support received in comparison to
                    other forms of support.
                  </li>
                </ul>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Complete your Schedule A filing requirements and e-file your{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Form 990-EZ with TaxZerone
                    </strong>
                    . It’s as simple as that!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990scha`}
                  >
                    E-File Your Form 990/990-EZ
                  </Button>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="scha"
                >
                  Schedule A filing Requirement
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section 501(c)(3) organizations that file Form 990/990-EZ
                  must complete and attach Schedule A along with their
                  information return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule A filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Reason for Public Charity Status
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part1-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part I showing lines 1–11, listing reasons an organization qualifies for public charity status."
                    width={760}
                    height={457}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization functions as a church or a
                  group of churches. The IRS looks at things like regular
                  worship services, religious teachings, and an organized
                  leadership setup to decide if an organization qualifies as a
                  church for tax purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization is a school that provides
                  formal education, with <br />
                  teachers, students, a curriculum, and regular classes at a
                  physical location. For details, see
                  <br />
                  <Link
                    href="/resources/tax-exempt/form-990-schedule-e/"
                    sx={{ color: '#0202ff' }}
                  >
                    Schedule E (Form 990)
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization operates a hospital or
                  provides medical care (including rehab centers or outpatient
                  clinics that function like hospitals). This does not include
                  medical schools, research-only groups, or nursing/children’s
                  homes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization actively conducts medical
                  research together with a qualifying hospital. Simply funding
                  others to do research doesn’t count your team must be directly
                  involved.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization raises and manages funds
                  to support a state-owned public college or university (like
                  managing donations, building campus facilities, or giving
                  student scholarships). This is for “friends of” or
                  foundation-type organizations that support public
                  universities, and you must complete Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Only check this if your organization is{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    federal, state, or local unit
                  </span>{' '}
                  and has an IRS letter saying it’s tax-exempt under{' '}
                  <Link
                    href="https://blog.taxzerone.com/posts/what-is-501c3-a-clear-guide-to-nonprofit-tax-exempt-status/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3)
                  </Link>
                  . Most nonprofits should not check this out.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization gets broad support from
                  the public (many donors, not just a few big ones) and meets
                  the IRS for public support tests. You’ll prove this by
                  completing Part II with your income numbers.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization is a community trust that
                  raises money for a specific city, region, or community and
                  meets the public support test. Even if you’re legally set up
                  as a trust (not a corporation), you still check Line 8 and
                  complete Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization does agricultural research
                  together with a college of agriculture (land-grant or
                  similar). Just list the college’s name and location -Part II
                  is not required.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your organization is publicly supported
                  through program fees, services, and small donors (not a few
                  big donors). You’ll need to complete Part III to prove this.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box only if the IRS has specifically approved your
                  organization as one that tests products or methods for public
                  safety.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part1-line12-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part1-line12-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part1-line12-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part I, line 12, indicating the organization qualifies as a publicly supported organization under section 509(a)(2)."
                    width={760}
                    height={531}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 12a – 12d First, Pick Your Supporting Organization Type
                  <br />
                  These lines apply only if your nonprofit mainly exists to
                  support other charities
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose this if the supported charity has{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    real control over your organization,
                  </span>{' '}
                  like appointing most of your board.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose this if the same people manage both organizations
                  (shared directors or officers).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose this if you work closely in daily operations with the
                  supported charity and meet the IRS tests in Part IV.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Choose this if you mainly provide money or resources and must
                  also complete Part V.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box if your IRS exemption letter already states
                  your supporting organization type (Type I, II, or III).If your
                  letter doesn’t mention a type, or it’s unclear, leave this
                  blank and choose the type that best fits how you actually
                  operate.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12f
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the number of organizations you support during the
                    year.
                  </li>
                  <li>
                    Include all the charities you are set up to support even if
                    you didn’t give them money this year{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use one row (A–E) for each supported organization. This table
                  shows who you support and how.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column i
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Write the full legal name of each charity you support.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column ii
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the EIN of that charity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column iii
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the line number from Lines 1-10 that best fits the
                  supported charity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column iv
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Choose Yes if this charity is written in your official
                  paperwork.If no, just explain briefly in Part VI why it isn’t
                  listed
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column v
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter how much cash you gave them this year.If none, enter 0.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column vi
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Estimate the value of goods, services, space, or other help
                  you provide.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Support Schedule for Organizations Described in
                  Sections 170(b)(1)(A)(iv) and 170(b)(1)(A)(vi)
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section A. Public support
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part2-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part2-img.png"
                    loading="lazy"
                    alt="IRS Form 990 Schedule A Part II lines 1–18 support schedule for organizations qualifying under public charity rules"
                    width={760}
                    height={354}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report normal donations and grants here (including forgiven
                  PPP loans when applicable). Don’t include payments for
                  tickets, services, or merchandise; those go on other lines.
                  Don't count donated services or free use of space/equipment.
                  Leave out “unusual grants” (large, rare gifts) and list them
                  separately in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter tax money a government collected for your benefit and
                  give to you (or spent on you). Report this even if you didn’t
                  treat it as revenue in your books.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of free office space or services provided by a
                  government unit just for you. Don’t include services that the
                  government provides to everyone for free.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line shows your total support from all sources for the
                  year, based on the amounts you entered above. Make sure this
                  total is correct, because it’s used to calculate your public
                  support percentage. A small mistake here can change whether
                  you qualify as a public charity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List the portion of any donor’s total gifts that exceeds 2% of
                  your total support. Combine gifts from related people (like
                  spouses) as one donor. This limit doesn’t apply to gifts from
                  government or publicly supported charities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line shows your public support percentage based on the
                  IRS formula. You generally need 33 …“% or more to qualify as
                  publicly supported. If it’s lower, you may still qualify under
                  special rules shown later.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B. Total Support
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part2-secb-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part2-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part2-secb-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Support Schedule for Organizations Described in Sections 170(b)(1)(A)(iv) and 170(b)(1)(A)(vi) Section B. Total Support"
                    width={760}
                    height={272}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include all donations, grants, and contributions that help
                  your organization qualify as a public charity. This shows that
                  your nonprofit is supported by a broad group of people, not
                  just a few large donors.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report on income from interest, dividends, rents, or
                  royalties. Only include earnings from investments, not from
                  activities directly related to your mission. Do not report
                  income from program activities here only passive investment
                  income counts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter net income from activities that are not related to your
                  mission. If the result is a loss, enter zero. This separates
                  business income from charitable support.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include any remaining support not reported on Lines 7-9.
                  Explain the source and nature of these amounts in Part VI for
                  clarity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Lines 7 through 10 to get your total support. This number
                  is used to calculate your public support percentage, so
                  accuracy is essential.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report total income from activities that directly further your
                  mission, such as program fees or mission-related sales. Do not
                  include unrelated business income.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box only if your organization is in its first five
                  years as a 501(c)(3). New organizations are not required to
                  complete the full public support test until the sixth year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C. Computation of public support percentage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part2-secc-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part2-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part2-secc-img.png"
                    loading="lazy"
                    alt="Section C of Form 990 Schedule A showing the calculation of the public support percentage and related financial figures."
                    width={760}
                    height={272}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check this box only if your organization is in its first five
                  years as a 501(c)(3). New organizations are not required to
                  complete the full public support test until the sixth year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter last year’s public support percentage from Schedule A.
                  This is used to check if you continue to qualify based on
                  prior-year results.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if your current-year public support is 33 …“% or
                  more. Your organization qualifies as publicly supported for
                  this year and the next.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if last year’s public support was 33 …“% or more.
                  Your organization qualifies as publicly supported for this
                  year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 17a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if your current-year public support is at least
                  10% and you meet the IRS “facts and circumstances” test.
                  Explain in Part VI how you met this test.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 17b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select this if last year’s public support was at least 10%,
                  and you meet the IRS requirements. Provide your explanation in
                  Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select this if none of the above applies. Your organization is
                  treated as a private foundation for filing purposes and must{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990-PF
                  </Link>{' '}
                  instead.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Support Schedule for Organizations Described in
                  Section 509(a)(2)
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part3-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part III lines 1–20 support schedule for organizations described in section 509(a)(2)"
                    width={760}
                    height={461}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amounts received during 2021 for each line (gifts,
                  tax revenues, services, etc.). This reflects only that year’s
                  activity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the same categories of support, but only for{' '}
                  <span style={{ fontWeight: 600 }}> 2022.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include the organization’s public support amounts{' '}
                  <span style={{ fontWeight: 600 }}> received in 2023.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amounts related to public support{' '}
                  <span style={{ fontWeight: 600 }}> for 2024. </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the public support figures{' '}
                  <span style={{ fontWeight: 600 }}> for 2025 </span>(the most
                  recent year in the five-year period).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add together columns (a) through (e).This column shows the
                  five-year total for each line and is used to calculate the
                  organization’s overall public support and public support
                  percentage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report true donations that support your mission. Do not
                  include unusual grants or donated services or facilities.
                  Count membership fees here only when they are supported, not
                  payments for goods or services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income from activities that directly carry out your
                  exempt purpose, such as program fees, admissions, or service
                  charges. Include membership fees paid to access your programs
                  or services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income from activities that are not treated as
                  unrelated business income, such as volunteer-run sales or
                  convenience services. These receipts belong here, not as
                  donations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report taxes collected by a government and provided for your
                  organization’s benefit, whether paid to you directly or spent
                  on your behalf.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the fair value of services or space provided to you by
                  a government at no charge, if these are not generally
                  available to the public.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the total through Line1 to Line5.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Identify how much Lines 1-3 came from disqualified persons and
                  total these amounts by year. Keep donor details in your
                  records, but do not attach them to your return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the portion of related-activity receipts from any
                  single donor or government agency that exceeds the allowed
                  threshold. Maintain internal records showing the payer and the
                  excess amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Share the Line 8 instruction text, and I’ll convert it into
                  the same clear, humanized format instantly.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B. Total support
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part3-secb-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part3-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part3-secb-img.png"
                    loading="lazy"
                    alt="Part III. Support Schedule for Organizations Described in Section 509(a)(2). Section B. Total Support"
                    width={760}
                    height={301}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the amounts for each listed line (public support,
                  investment income, other income, etc.) for the year 2021 only.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the same categories of total support amounts, but only
                  for <span style={{ fontWeight: 600 }}> 2022.</span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include all applicable total support figures
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    received or earned in 2023
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the organization’s total support amounts
                  <span style={{ fontWeight: 600 }}> for 2024</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total support figures
                  <span style={{ fontWeight: 600 }}> for 2025,</span> the most
                  recent year in the five-year period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.3 } }}>
                  <li>Add together columns (a) through (e). </li>
                  <li>
                    This column shows the combined five-year total for each line
                    and is used to determine overall total support and calculate
                    the public support percentage.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report income your organization earned from passive sources
                  such as interest, dividends, rent, royalties, or similar
                  earnings. Do not include income from activities that directly
                  support your mission. If the income comes from your regular
                  programs or services, report it under program revenue instead.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total gross income your organization received from
                  interest, dividends, rents, royalties, securities lending, and
                  similar sources. This line is only for passive income. If the
                  money was earned through activities that support your exempt
                  purpose, it should be reported as program revenue, not here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the net taxable income your organization earned from
                  unrelated business activities that started or were acquired
                  after June 30, 1975, after subtracting the related tax.
                  Include membership fees only when they are payments for goods,
                  services, admissions, or facility use connected to these
                  unrelated business activities.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add Line 10a and 10b together.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the net earnings from any unrelated business activities
                  that are not already reported under Line 10b. Do not include
                  income from unrelated businesses that began before July 1,
                  1975. If these activities result in a loss, enter zero.
                  Membership fees paid for goods or services in these unrelated
                  activities belong here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 12
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any remaining support your organization received that
                  was not reported in the earlier lines of this section. Briefly
                  describe the source and nature of this support in the
                  explanation section. Do not include gains or losses from
                  selling assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 13
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate this by adding together the amounts reported on
                  Lines 9, 10c, 11, and 12.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 14
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization is within its first five years of
                  qualifying as a public charity, check this box and stop here.
                  You do not need to complete the public support or investment
                  income calculations yet. From the sixth year onward, you must
                  complete the remaining lines and apply for the public support
                  tests.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C. Computation of Public Support Percentage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part3-secc-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part3-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part3-secc-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A, Section C, displaying the worksheet used to compute the organization’s public support percentage."
                    width={760}
                    height={50}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 15
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your public support percentage for this year. Round it
                  into two decimal positions. (for example, 58.3456 rounded to
                  58.35%).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 16
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter last year’s public support percentage from Schedule A
                  (Form 990), Part III, Line 15. Round it into two decimal
                  places.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '17px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section D. Computation of Investment Income percentage
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part3-secd-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part3-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part3-secd-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A, Section D, showing the calculation of the organization’s investment income percentage."
                    width={760}
                    height={129}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 17
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Show the investment income percentage, rounded to a whole
                  number.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 18
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For 2025, carry forward the 2024 investment income percentage
                  (Schedule A, Part III, Line 17) and round to a whole number.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 19a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 14 isn’t checked, Line 15 is above 33 …“%, and Line 17
                  is 33 …“% or less, check this box and stop. The organization
                  qualifies as publicly supported for 2025 and 2026.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 19b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Line 14 or 19a isn’t checked, Line 16 is above 33 …“%, and
                  Line 18 is 33 …“% or less, check this box and stop. The
                  organization qualifies as publicly supported for 2025.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 20
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If none of the above boxes apply, check this box. The
                  organization doesn’t meet the public support test for 2025 and
                  must file Form 990-PF as a{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    private foundation
                  </Link>
                  .
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <strong style={{ fontWeight: 600 }}> Tips:</strong> If this is
                  the organization’s sixth year as a 501(c)(3), it may still
                  qualify based on its first five years. If eligible, explain in
                  Part VI.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV-Supporting Organizations
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section A. All Supporting organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part IV. Supporting Organizations Section A. All Supporting Organizations Line 1 to Line 4"
                    width={760}
                    height={444}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Confirm that your governing document clearly names (or
                  defines) the organizations you support.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Check Yes if you support only those listed. </li>
                  <li>
                    If you support any organizations beyond those listed, select
                    No and briefly explain in Part VI how your organization
                    decides which groups to support{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you supported any organization that doesn’t yet have an IRS
                  public-charity determination, check Yes and explain in Part VI
                  how you verified it qualifies as a public charity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you support a 501(c)(4), (5), or (6) organization that
                  meets public-support rules, check Yes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Make sure each year that the 501(c)(4), (5), or (6)
                    organization you support still meets the public-support
                    rules.
                  </li>
                  <li>
                    Check <span style={{ fontWeight: 600 }}> Yes</span> and
                    briefly explain in Part VI how you confirmed this (for
                    example, by checking their records or filings).
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Make sure any support given to a{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    501(c)(4), (5), or (6) group
                  </span>
                  is used only for charitable purposes. Check Yes if you have
                  controls in place and explain them in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An organization cannot qualify as a Type III supporting
                  organization for the tax year if it supports any organization
                  that is not organized in the United States. All supported
                  organizations must be U.S.-based to meet Type III eligibility
                  requirements.
                </Typography>{' '}
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you provide funds to any organization that is not
                  recognized as a 501(c)(3), you must show that you retained
                  control over how the funds were used. This means setting
                  conditions, monitoring use of funds, and ensuring the money
                  was used only for charitable purposes. Briefly describe your
                  controls in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you supported a foreign organization, you must confirm that
                  you maintained oversight of the funds and ensured they were
                  used solely for charitable purposes. If the foreign
                  organization does not have an IRS determination, explain in
                  Part VI the safeguards you used to protect your charitable use
                  of funds.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-line5-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-line5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-line5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part IV. Supporting Organizations Section A. All Supporting Organizations line 5 to line 8"
                    width={760}
                    height={274}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Say whether you added, removed, or replaced any supported
                  organizations during the year. If yes, explain the change in
                  Part VI (who changed, why, and how it was approved).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm whether any new or replaced organization was already
                  included in the class listed in your governing document.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm whether a replacement happened because of a situation
                  you couldn’t control, such as a supported organization losing
                  its public charity status.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm whether all your activities were carried out only to
                  support your supported organization(s). If you provide grants,
                  services, or benefits to anyone else, explain the details in
                  Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Lines 7 & 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  State whether you made any payments or loans to substantial
                  contributors, their family members, or entities they control.
                  These transactions are treated as improper benefits under
                  Internal Revenue Service rules and must be disclosed on
                  Schedule L if they occur.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-line9-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-line9-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-line9-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part IV. Supporting Organizations Section A. All Supporting Organizations line 9a to line 10b"
                    width={760}
                    height={196}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Say whether anyone who isn’t allowed to control the
                  organization actually has a say in how it runs.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Say whether any such person has money invested in, or
                  ownership ties to, your organization or its assets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Indicate whether any disqualified person personally benefits
                  from your organization’s property or resources. If yes,
                  explain briefly in Part VI as required by the Internal Revenue
                  Service.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  State whether your organization is subject to excess business
                  holding rules. Certain Type II and Type III supporting
                  organizations can trigger excise taxes under Internal Revenue
                  Service rules when accepting contributions from controlling
                  persons or related parties.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-line11-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-line11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-line11-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part IV. Supporting Organizations Section A. All Supporting Organizations line 11"
                    width={760}
                    height={131}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if the organization receives any gifts from
                  someone who controls a supported organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if any contributions came from close family
                  members of a person who controls a supported organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 11c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer “Yes” if any donations were received from a business or
                  entity that is owned or controlled by a person who controls a
                  supported organization.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B. Type I Supporting organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-secb-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-secb-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A, Section B, lines 1 and 2, identifying Type I supporting organizations and related qualification details."
                    width={760}
                    height={192}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that one or more supported organizations actively
                  control your organization. This means they have real authority
                  over your leadership and key decisions (for example, they can
                  appoint or remove most of your board). If control is shown in
                  another way, select “No” and explain how this control works in
                  Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You may support organizations that do not control you, but
                  only when doing so clearly advances the mission and goals of
                  the organizations that do control you.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C. Type II Supporting Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-secc-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-secc-img.png"
                    loading="lazy"
                    alt="Section C. Type II Supporting Organizations"
                    width={760}
                    height={80}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Confirm that your organization and its supported
                  organization(s) are overseen by the same people. This shared
                  leadership helps ensure your organization follows the needs
                  and direction of those it supports. If this connection is
                  maintained in another way (not mainly through shared board
                  members), select “No” and briefly explain the arrangement in
                  Part VI.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section D. All Type III Supporting Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-secd-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-secd-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A, Section D, lines 1–3, detailing requirements and status for all Type III supporting organizations."
                    width={760}
                    height={193}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you’re a Type III supporting organization, you must send a
                  yearly update to each supported organization. Tell them what
                  support you provided last year, share your latest Form 990
                  (you can hide donor names/addresses), and include any updated
                  governing documents. Make sure this is sent on time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that your supported organization has a real connection
                  with you, such as shared board members, appointment power, or
                  a close working relationship. If it’s based mainly on a
                  long-standing relationship, explain it in Part V.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Confirm that your supported organization has a genuine role in
                  key decisions, such as how funds are invested or grants are
                  made. If so, briefly describe their involvement in Part VI.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section E. Type III Functionally Integrated Supporting
                  Organizations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part4-sece-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part4-sece-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part4-sece-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Section E. Type III Functionally Integrated Supporting Organizations Line 1 to Line 3"
                    width={760}
                    height={346}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    If your organization is classified as a Type III supporting
                    organization, it can’t just exist to send funds once in a
                    while. There needs to be a real connection between you and
                    the organization you support. In simple terms, your
                    involvement should actually matter to their work.
                  </li>
                  <li>
                    The IRS looks at this requirement in three possible
                    ways.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  This applies if your organization actively runs programs or
                  services that directly help the supported organization achieve
                  its mission. In other words, you’re not just giving financial
                  help; you’re doing work that supports their purpose in a
                  hands-on way.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For example, if the supported organization focuses on
                  education, and your organization runs tutoring programs that
                  further that same mission, that would generally meet this
                  test.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You function as the parent organization, meaning you have real
                  oversight or governing authority over the supported
                  organization.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You support only government-based organizations, and a large
                  part of your work directly advances their public mission. If
                  you support more than one, they must either operate in the
                  same area or work closely together on shared efforts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    To meet this test, most of what your organization does must
                    directly carry out the mission of the organization you
                    support.{' '}
                  </li>
                  <li>
                    You must be doing the actual charitable work yourself, not
                    just fundraising, investing money, or giving grants. The
                    work should also be something the supported organization
                    would normally do on its own if you weren’t doing it for
                    them.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your organization and the supported organizations must operate
                  together as one unified system (not separately).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Your organization should guide the big decisions like
                  planning, policies, and budgets.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Your organization must have the power to appoint and remove
                  most of the board members or leaders of each supported
                  organization.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 1.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V Type III Non-Functionally integrated 509(a)(3)
                  Supporting Organizations
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section A. Adjusted net income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part5-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part V Type III Non-Functionally Integrated 509(a)(3) Supporting Organizations"
                    width={760}
                    height={244}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Shows last year’s income and expenses. These numbers are used
                  to calculate how much must be distributed this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (B)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Shows this year’s figures. It is mainly for comparisons and
                  tracking.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter your net short-term capital gain, if any.</li>
                  <li>Do not include long-term capital gains or losses. </li>
                  <li>
                    Short-term capital losses cannot be carried out in other
                    years.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any amounts you previously treated as distributions but
                  later received back. This includes repayments, proceeds from
                  sale of previously distributed property, or unused set-aside
                  amounts.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter all other income earned during the year (including
                  business income and tax-exempt bond income).Do not include
                  gifts, grants, contributions, long-term capital gains/losses,
                  certain trust or estate distributions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Add Lines <span style={{ fontWeight: 600 }}> 1</span> through{' '}
                  <span style={{ fontWeight: 600 }}>3</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You may deduct depreciation (straight-line method only) and
                  allowable depletion expenses.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the regular and necessary expenses directly connected
                    to earning income. This can include{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      salaries, rent, interest, and taxes
                    </span>{' '}
                    related to income-generating activities.{' '}
                  </li>
                  <li>
                    If a property is used partly to earn income and partly for
                    charitable work, only report the portion of expenses related
                    to the income-earning use.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Enter any other permitted income-related expenses.</li>
                  <li>
                    Do not include capital losses, charitable contributions, net
                    operating losses, or dividend deductions.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Lines 5–7 from Line 4. This final amount is your
                  <span style={{ fontWeight: 600 }}> Adjusted Net Income</span>.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section B. Minimum Asset Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part5-secb-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part5-secb-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part5-secb-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part V. Type III Non-Functionally Integrated 509(a)(3) Supporting Organizations Section B. Minimum Asset Amount"
                    width={760}
                    height={287}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (A)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Lists last year’s asset values. These amounts help determine
                  the minimum distribution requirement.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (B)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Shows current year asset values. This is optional and used for
                  internal reference.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This is the Total Value of Your Investment Assets. List the
                  combined market value of any property or funds not used for
                  your daily mission. If an item was only owned for part of
                  2026, follow the guide to calculate its average value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Securities:</span> Use the
                  average monthly value of stocks, bonds, or mutual funds. Pick
                  a consistent method (first/last day of month, or average).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Cash: </span> Average your
                  cash on hand for each month. Include all cash, even if
                  reserved for distributions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Other Assets: </span> Value
                  property or other assets once a year. New valuation is needed
                  each year, unless using a certified 5-year real estate
                  appraisal.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Total: </span> Add above
                  lines 1a, 1b, 1c and mark the total.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}> Discounts: </span> If
                  assets lose value due to size, marketability, or other
                  reasons, report the discount and explain in Part VI. Max
                  discount for securities: 10%.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report loans or mortgages on these assets. If you held an
                  asset part of the year, adjust the debt for that period.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Take the total value of your assets from{' '}
                  <span style={{ fontWeight: 600 }}> line 1d</span> and subtract
                  any debts reported on{' '}
                  <span style={{ fontWeight: 600 }}>line 2</span>. The result is
                  the net asset value you’ll use for your calculations.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can set aside some cash from your minimum assets to cover
                  everyday costs, like office expenses or normal spending for
                  your charitable work. Normally, this is about 1.5% of your
                  total assets (after any debts). If you need more to cover your
                  actual expenses, that’s fine just explain the reason in Part
                  VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Take the total value of your non-exempt-use assets by
                  subtracting the cash set aside for expenses (line 4) from your
                  total assets (line 3).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Multiply the result from line 5 by 3.5% (0.035) to calculate
                  the required minimum amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you have any recoveries from prior-year distributions (from
                  Section A, line 2), enter that amount here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Add the recoveries (line 7) to your calculated 3.5% of
                  non-exempt assets (line 6) to get your{' '}
                  <span style={{ fontWeight: 600 }}> Minimum Asset Amount</span>
                  .
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section C. Distributable Amount
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part5-secc-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part5-secc-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part5-secc-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part V. Type III Non-Functionally Integrated 509(a)(3) Supporting Organizations Section C. Distributable Amount"
                    width={760}
                    height={176}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your adjusted net income from last year (from Section A,
                  line 8, column A). This is the starting point to figure out
                  how much your organization should distribute.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate 85% of the amount from line 1. This gives an
                  estimate of the portion of last year’s income that you
                  generally need to set aside for charitable distributions
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your minimum required assets from last year (from
                  Section B, line 8, column A). This makes sure you keep enough
                  assets to support your charitable work.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Look at the numbers from line 2 and line 3. Enter the bigger
                  number here. This makes sure you meet the higher of the two
                  rules either based on income or assets, so your distributable
                  amount is enough.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any income tax your organization paid in the prior year.
                  This reduces the amount you must distribute.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Calculate your Distributable Amount by subtracting line 5 from
                  line 4.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Select this box if this is the first year your organization is
                  being treated as a non-functionally integrated Type III
                  supporting organization. If you’re not sure whether this
                  applies to you, take a moment to review the detailed
                  instructions before making your selection.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section D. Distributions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part5-secd-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part5-secd-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part5-secd-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part V. Type III Non-Functionally Integrated 509(a)(3) Supporting Organizations Section D. Distributions"
                    width={760}
                    height={210}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the amounts you paid directly to supported organizations
                  to help them carry out their charitable (501(c)(3)) purposes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report amounts you spent on activities that directly support
                  the charitable work of your supported organizations, but only
                  the portion where expenses were more than any income earned
                  from that activity.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include reasonable and necessary administrative expenses that
                  help accomplish the supported organizations’ charitable
                  purposes. Do not include investment-related costs or general
                  fundraising expenses (with limited exceptions for certain
                  contributions received directly by a supported organization).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report amounts used to purchase assets that will be used for
                  charitable purposes (called exempt-use assets). These assets
                  must be used by your organization or made available to
                  supported organizations at no cost or for a very small fee.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any money you set aside this year for a specific
                  charitable project for a supported organization. It counts for
                  this year once approved -not again when it’s later paid out.
                  You must get written approval from both the supported
                  organization and the IRS by filing Form 8940 before year-end.
                  Mention in Part VI if you receive approval.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add lines <span style={{ fontWeight: 600 }}> 1</span> through{' '}
                  <span style={{ fontWeight: 600 }}>5</span>. This is the total
                  amount distributed for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the portion of distributions from line 1 that were given
                  to supported organizations that were actively involved with
                  (attentive to) and engaged with (responsive to) your
                  organization during the year. Provide details in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this line to calculate what percentage of your total
                  distributable amount (from Section C) was given to supported
                  organizations that met both the attentiveness and
                  responsiveness requirements.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 9
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    For a Type III non-functionally integrated supporting
                    organization, at least{' '}
                    <span style={{ fontWeight: 600 }}> one-third (33 …“%)</span>{' '}
                    of your distributable amount must go to supported
                    organizations that meet both tests.
                  </li>
                  <li>
                    If this percentage is less than one-third, you may not
                    qualify for this status and could be treated as a private
                    foundation instead.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: '16px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Section E. Distribution Allocations
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part5-sece-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part5-sece-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part5-sece-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A Part V. Type III Non-Functionally Integrated 509(a)(3) Supporting Organizations Section E. Distribution Allocations"
                    width={760}
                    height={288}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Excess Distributions: Shows any amount the organization gave
                  more than required in previous years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (ii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Under distributions Pre-2025: Shows any amount the
                  organization did not distribute enough in prior years (before
                  2025).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (iii)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Distributable Amount for 2025: Shows the amount the
                  organization must distribute in 2025 based on current
                  calculations, taking prior over- or under-distributions into
                  account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter your 2025 distributable amount from Section C, line 6.
                  This is the amount your organization is required to distribute
                  for the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If 2024 was your first year as a Type III non-functionally
                  integrated supporting organization, your required distribution
                  for that year was zero. If you didn’t distribute enough in
                  2023 or 2024, you may lose this status and be treated as a
                  private foundation unless you qualified for a special
                  exception. If so, explain it in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This part is just about checking whether you still have any
                  extra distribution amounts left from previous years and how
                  they’re being used now.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write down any extra amounts from 2020 that you haven’t used
                  yet. These are the amounts you gave above the required minimum
                  this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write down any extra amounts from 2020 that you haven’t used
                  yet. These are the amounts you gave above the required minimum
                  this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write down any extra amounts from 2020 that you haven’t used
                  yet. These are the amounts you gave above the required minimum
                  this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write down any extra amounts from 2020 that you haven’t used
                  yet. These are the amounts you gave above the required minimum
                  this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Write down any extra amounts from 2020 that you haven’t used
                  yet. These are the amounts you gave above the required minimum
                  this year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add those amounts together. This shows how much extra you
                  currently have available. Now you apply that extra amount step
                  by step.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  First, use it to cover anything you may have fallen short on
                  in earlier years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3h
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If there’s still some left, apply it toward your 2025 required
                  amount.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3i
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any leftover from 2020 is too old to use anymore (because
                  it passed the 5-year limit), show it here.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3j
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After subtracting what you used (and what expired) from your
                  total, the remaining balance is what you can still carry
                  forward.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use your current-year distributions (from Section D, line 7).
                  First apply them to any unpaid required amounts from prior
                  years. If any amount remains, apply it to this year’s required
                  distribution. If there is still an excess after covering both,
                  you can carry that remaining amount forward to future years.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Now check whether you met this year’s required distribution.
                  Add together your prior-year carryovers and current-year
                  distributions and compare that total to your required
                  distributable amount. If the required amount is more than what
                  you distributed, the requirement has not been met.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the full required amount was not distributed, the
                  organization generally does not qualify as a Type III
                  non-functionally integrated supporting organization for that
                  year and may be treated as a private foundation, requiring the
                  filing of{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-pf-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-PF
                  </Link>
                  . However, if the shortfall was due to reasonable cause or a
                  legal restriction, an exception may apply. If so, explain the
                  details clearly in Part VI.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any excess distributions from the past five years that
                  you haven't used yet. These leftovers can be applied toward
                  your current requirements to save you money.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Show how the total carryover on line 7 is made from each
                  previous year. List the amounts left over from 2021, 2022,
                  2023, 2024, and 2025 separately. This helps keep track of
                  which year each carryover came from.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part VI - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-scha-part6-img.webp" />
                  <source srcSet="/assets/images/f990-scha-part6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-scha-part6-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule A, Part VI, Supplemental Information section for providing additional explanations and required narrative details. "
                    width={760}
                    height={493}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this section to explain or give more details for any
                  answers on Schedule A (Form 990). Be sure to mention the part
                  and line number your information relates to.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you need more space, you can add extra pages.{' '}
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Caution:</strong> Do not
                  include any donor, grantor, or contributor names here, since
                  Part VI is available for public inspection.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule A filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong style={{ fontWeight: 600 }}>TaxZerone</strong> is an
                  IRS-authorized e-file service provider; meaning you get
                  instant updates on your{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    990/990-EZ return
                  </Link>{' '}
                  filing status. We ensure help is available in every step to
                  provide you with a smooth e-filing experience!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your exempt organization&apos;s
                  information return due to any reason, you can correct and
                  retransmit it to the IRS for free!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your 990/990-EZ return with Schedule A
                  attachment is transmitted to the IRS - just 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details
                    </strong>{' '}
                    - Choose the tax year for which you want to file a return,
                    and provide your organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return
                    </strong>{' '}
                    - Complete Schedule A and preview the information provided
                    in the return for accuracy before transmitting.
                  </li>
                  <li>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS
                    </strong>{' '}
                    - Transmit Schedule A along with your 990/990-EZ return to
                    the IRS and get the acceptance in just a few hours.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'column' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Ready to attach Schedule A along with your 990/990-EZ
                        return with TaxZerone?
                      </Typography>
                      <Typography variant="body2">
                        Make the e-filing process simple and hassle-free by
                        clicking the button below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 400, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990scha`}
                      >
                        File Exempt Organization Form Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        <Link
                          href="https://www.irs.gov/instructions/i990sa"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions
                        </Link>{' '}
                        <span style={{ fontWeight: 600 }}>for Schedule A</span>{' '}
                        (Form 990 or 990-EZ) – Public Charity Status and Public
                        Support
                      </Typography>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          Instructions
                        </Link>{' '}
                        <span style={{ fontWeight: 600 }}>for Form 990</span> –
                        Return of Organization Exempt from Income Tax
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 87,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the purpose of Schedule A?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    The purpose of filing Schedule A is to provide more detailed
                    information about public charity status and public support.
                    Organizations that are described in Internal Revenue Code
                    Section 501(c)(3) and are public charities must complete and
                    attach Schedule A.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Is there a filing deadline for Schedule A?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      There&apos;s no separate filing deadline for Schedule A as
                      it aligns with the deadline for the standard Form
                      990/990-EZ return.
                    </Typography>
                    <Typography>
                      In most cases, the due date to file a 990/990-EZ return is
                      on the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        15th day of the 5th month
                      </strong>{' '}
                      after the end of your organization&apos;s fiscal year. For
                      organizations that follow the calendar year (ending on
                      December 31), the due date is{' '}
                      <strong style={{ fontWeight: 600 }}>May 15th</strong>.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What tests are required to qualify as a publicly supported
                  organization?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    To qualify as a publicly supported organization, an entity
                    must meet specific support tests, which are used to
                    determine the extent of its public support. The two primary
                    tests used for this purpose are the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      Public support test
                    </strong>{' '}
                    and the{' '}
                    <strong style={{ fontWeight: 600 }}>
                      One-third support test
                    </strong>{' '}
                    (Public Support Test under Section 509 a(2)).
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. Who needs to complete Schedule A for Form 990-EZ?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Organizations that need to report their public charity
                    status and public support information must complete{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 990-EZ Schedule A
                    </span>{' '}
                    and include it with their return. This schedule is generally
                    required for organizations recognized under section{' '}
                    <Link
                      href="/resources/tax-exempt/501c3-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      501(c)(3)
                    </Link>{' '}
                    and certain other{' '}
                    <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                      tax-exempt organizations
                    </Link>{' '}
                    to demonstrate that they continue to meet the IRS public
                    support requirements.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcA;
